import * as React from 'react';

import {StackLegacy} from 'shells/layout/stack/stack-legacy';

import {AiFeatureType} from 'nutshell-graphql-types';

import {analyticsClient} from '../../analytics-client';
import {usePoll} from '../../util/hooks';

import {useIsAiFeatureEnabled} from '../setup/ai-features/graphql/hooks/use-is-ai-feature-enabled';

import {useGetCallActivity, useUpdatePhonecallShouldSummarize} from './graphql/hooks';
import {SummarizeSection} from './summarize-section';
import {TranscriptionSection} from './transcription-section';

type Props = {
    callHasEnded: boolean;
    shouldTranscribe: boolean;
    shouldSummarize: boolean;
    phonecallId: string | null | undefined;
    activityId: string | null | undefined;
};

export function DialerTranscriptionSection(props: Props) {
    const {callHasEnded, shouldTranscribe, shouldSummarize, phonecallId, activityId} = props;

    const {isEnabled: isClickToCallNotetakerEnabled, isLoading: isAiFeatureLoading} =
        useIsAiFeatureEnabled(AiFeatureType.CLICK_TO_CALL_NOTETAKER);

    const shouldSummarizeState =
        NutClientConfig.canUseAiToken && isClickToCallNotetakerEnabled ? shouldSummarize : false;

    const analyticsFired = React.useRef(false);

    const {activity, startPolling, stopPolling} = useGetCallActivity(activityId);
    const {updatePhonecallShouldSummarize} = useUpdatePhonecallShouldSummarize();

    const shouldPollTranscription = Boolean(
        shouldTranscribe && activity && !activity.transcription
    );
    const shouldPollSummary = Boolean(
        shouldSummarizeState && activity && (!activity.summary || !activity.summary.summary)
    );
    const shouldPoll = shouldPollTranscription || shouldPollSummary;

    // Handles initiation and cleanup of polling with using the shouldPoll
    // state. Will stop polling on unmount.
    usePoll({
        shouldPoll,
        startPolling,
        stopPolling,
    });

    const summary = activity && activity.summary && activity.summary.summary;
    const summaryStatus = activity && activity.summary && activity.summary.status;
    const transcription = activity && activity.transcription;

    React.useEffect(() => {
        if (phonecallId && !isAiFeatureLoading) {
            updatePhonecallShouldSummarize({
                phonecallId,
                shouldSummarize: shouldSummarizeState,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [phonecallId, shouldSummarizeState, isAiFeatureLoading]);

    React.useEffect(() => {
        if (analyticsFired.current) return;
        if (!callHasEnded || isAiFeatureLoading) return;
        analyticsFired.current = true;
        analyticsClient.track(analyticsClient.EVENTS.TRACK_C2C_CALL_COMPLETED, {
            shouldTranscribe: shouldTranscribe,
            shouldSummarize: shouldSummarizeState,
        });
    }, [shouldTranscribe, shouldSummarizeState, callHasEnded, isAiFeatureLoading]);

    if (!shouldSummarizeState || !NutClientConfig.canUseAiToken) {
        return null;
    }

    return (
        <StackLegacy spacing={16}>
            <div className='mt-16'>
                <SummarizeSection
                    // @ts-expect-error
                    activityId={activityId}
                    summary={summary}
                    summaryStatus={summaryStatus}
                    callHasEnded={callHasEnded}
                />
            </div>
            <TranscriptionSection
                callHasEnded={callHasEnded}
                shouldTranscribe={shouldTranscribe}
                transcription={transcription}
            />
        </StackLegacy>
    );
}
